import type { ExpectedHit } from "../../lib/rhythm/rhythmEngine";
import type { StrumDirection, StrumGesture } from "../../lib/rhythm/rhythmPathPlan";

export interface RhythmBeatTrackerProps {
  /** Beat actual 0–3 dentro del compás (metrónomo). */
  beat: number;
  /** Progreso fraccional 0–1 dentro del beat actual (opcional, más fluido). */
  beatProgress?: number;
  /** Si true, resalta subdivisiones (&). */
  showAnds?: boolean;
  className?: string;
  /** Modo rasgueo: flechas ↑↓ / mute por subdivision. */
  strumMode?: boolean;
  /** Eventos del ejercicio (para mapear flechas al compás actual). */
  expected?: ExpectedHit[];
  /** Beats absolutos desde t=0 (para saber el compás). */
  sessionBeats?: number;
}

type SlotMark =
  | { kind: "stroke"; direction: StrumDirection; gesture: StrumGesture }
  | { kind: "mute" }
  | { kind: "empty" };

function markForSlot(
  expected: ExpectedHit[] | undefined,
  measureStartBeat: number,
  slotIndex: number,
  slotBeats: number
): SlotMark {
  if (!expected?.length) return { kind: "empty" };
  const slotStart = measureStartBeat + slotIndex * slotBeats;
  const slotEnd = slotStart + slotBeats;
  const hit = expected.find(
    (h) => h.beatOffset >= slotStart - 1e-6 && h.beatOffset < slotEnd - 1e-6
  );
  if (!hit) return { kind: "empty" };
  if (hit.isRest || hit.gesture === "mute") return { kind: "mute" };
  return {
    kind: "stroke",
    direction: hit.direction ?? "down",
    gesture: hit.gesture ?? "hand",
  };
}

function SlotArrow({ mark, active }: { mark: SlotMark; active: boolean }) {
  if (mark.kind === "empty") {
    return <span className="h-5 text-white/10 text-xs">·</span>;
  }
  if (mark.kind === "mute") {
    return (
      <span
        className={`text-[11px] font-semibold ${
          active ? "text-amber-200" : "text-white/30"
        }`}
      >
        ✕
      </span>
    );
  }
  const up = mark.direction === "up";
  return (
    <span
      className={`text-lg leading-none font-bold transition-colors ${
        active ? "text-[#99f6e4] scale-125" : "text-white/40"
      }`}
      aria-hidden
    >
      {up ? "↑" : "↓"}
    </span>
  );
}

/**
 * Tracker de tiempo musical a ancho completo: 1 & 2 & 3 & 4 &.
 * En modo strum añade flechas ↑↓ / mute por subdivision.
 */
export function RhythmBeatTracker({
  beat,
  beatProgress = 0,
  showAnds = true,
  className = "",
  strumMode = false,
  expected,
  sessionBeats = 0,
}: RhythmBeatTrackerProps) {
  const slots = showAnds
    ? ([
        { label: "1", strong: true },
        { label: "&", strong: false },
        { label: "2", strong: true },
        { label: "&", strong: false },
        { label: "3", strong: true },
        { label: "&", strong: false },
        { label: "4", strong: true },
        { label: "&", strong: false },
      ] as const)
    : ([
        { label: "1", strong: true },
        { label: "2", strong: true },
        { label: "3", strong: true },
        { label: "4", strong: true },
      ] as const);

  const strongIdx = Math.max(0, Math.min(3, beat));
  const slotIndex = showAnds
    ? strongIdx * 2 + (beatProgress > 0.5 ? 1 : 0)
    : strongIdx;
  const playheadPct =
    ((slotIndex + Math.min(1, Math.max(0, beatProgress)) * (showAnds ? 0.5 : 1)) /
      slots.length) *
    100;

  const measureStart = Math.floor(Math.max(0, sessionBeats) / 4) * 4;
  const slotBeats = showAnds ? 0.5 : 1;

  return (
    <div
      className={`relative w-full rounded-xl border px-3 py-2.5 sm:px-4 ${
        strumMode
          ? "border-[#99f6e4]/25 bg-black/45 py-3.5 shadow-[0_0_24px_rgba(153,246,228,0.08)]"
          : "border-white/10 bg-black/35"
      } ${className}`}
      role="status"
      aria-label={`Tiempo ${beat + 1} del compás`}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[9px] uppercase tracking-[0.14em] text-white/35">
          {strumMode ? "Rhythm tracker · rasgueo" : "Rhythm tracker"}
        </p>
        <p className="text-[10px] tabular-nums text-white/40">
          Beat {beat + 1}/4
        </p>
      </div>
      <div className="relative">
        <div
          className="pointer-events-none absolute -top-0.5 z-10 h-0 w-0 -translate-x-1/2 border-x-[5px] border-t-[7px] border-x-transparent border-t-[#99f6e4] transition-[left] duration-75"
          style={{ left: `${Math.min(97, Math.max(2, playheadPct))}%` }}
          aria-hidden
        />
        <div
          className="grid w-full gap-1.5"
          style={{ gridTemplateColumns: `repeat(${slots.length}, minmax(0, 1fr))` }}
        >
          {slots.map((s, i) => {
            const on =
              i === slotIndex ||
              (showAnds && i === strongIdx * 2 && beatProgress < 0.5);
            const passed = i < slotIndex;
            const mark = strumMode
              ? markForSlot(expected, measureStart, i, slotBeats)
              : ({ kind: "empty" } as SlotMark);
            return (
              <div
                key={`${s.label}-${i}`}
                className="flex min-w-0 flex-col items-center gap-1"
              >
                {strumMode && (
                  <div
                    className={`flex h-7 w-full items-center justify-center rounded-md transition-colors ${
                      on ? "bg-[#99f6e4]/15" : "bg-transparent"
                    }`}
                  >
                    <SlotArrow mark={mark} active={on} />
                  </div>
                )}
                <div
                  className={`w-full rounded-sm transition-colors duration-100 ${
                    strumMode ? "h-3" : "h-2.5"
                  }`}
                  style={{
                    background: on
                      ? "#99f6e4"
                      : passed
                        ? "rgba(153,246,228,0.4)"
                        : s.strong
                          ? "rgba(255,255,255,0.14)"
                          : "rgba(255,255,255,0.07)",
                  }}
                />
                <span
                  className={`text-[10px] tabular-nums sm:text-[11px] ${
                    s.strong
                      ? on
                        ? "font-semibold text-[#99f6e4]"
                        : "text-white/55"
                      : on
                        ? "text-white/70"
                        : "text-white/25"
                  }`}
                >
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      {strumMode && (
        <p className="mt-2 text-center text-[10px] text-white/35">
          ↓ abajo · ↑ arriba · ✕ muteo
        </p>
      )}
    </div>
  );
}
